import { useState, useRef } from "react";
import { supabase } from "@lib/supabase/client";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { cn } from "@shared/utils/utils";

interface ClinicImageUploadProps {
  currentImageUrl?: string | null;
  onUploadComplete: (url: string) => void;
  folder: string;
  aspectRatio?: "square" | "wide";
  placeholder?: string;
  className?: string;
}

const ClinicImageUpload = ({
  currentImageUrl,
  onUploadComplete,
  folder,
  aspectRatio = "square",
  placeholder = "اختر صورة",
  className,
}: ClinicImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - only allow specific image formats
    if (!allowedImageTypes.includes(file.type)) {
      toast.error("يرجى رفع صورة بصيغة PNG أو JPG أو WEBP فقط");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت");
      return;
    }

    setIsUploading(true);

    try {
      // Create a unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("clinic-images")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("clinic-images")
        .getPublicUrl(fileName);

      setPreviewUrl(publicUrl);
      onUploadComplete(publicUrl);
      toast.success("تم رفع الصورة بنجاح");
    } catch (error: unknown) {
      console.error("Upload error:", error);
      toast.error("حدث خطأ أثناء رفع الصورة");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onUploadComplete("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {previewUrl ? (
        <div className="relative group">
          <div
            className={cn(
              "rounded-lg overflow-hidden border bg-muted",
              aspectRatio === "square" ? "aspect-square w-32" : "aspect-video w-full max-w-md"
            )}
          >
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 ml-1" />
              تغيير
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={cn(
            "flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-colors hover:border-primary hover:bg-primary/5",
            aspectRatio === "square" ? "w-32 h-32" : "w-full max-w-md aspect-video",
            isUploading && "opacity-50 cursor-not-allowed"
          )}
        >
          {isUploading ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">{placeholder}</span>
              <span className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ClinicImageUpload;
